import { fromEvent, merge, Observable, of, race, ReplaySubject } from 'rxjs';
import {
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    map,
    pairwise,
    shareReplay,
    take,
    tap,
    throttleTime
} from 'rxjs/operators';

import { DOCUMENT, ViewportScroller } from '@angular/common';
import { afterRender, computed, effect, Inject, Injectable, NgZone, signal, Signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, Scroll } from '@angular/router';
import { PlatformService } from '@core/services/platform.service';
import { SiteContextService } from '@core/services/site-context.service';
import { UrlHelperService } from '@core/services/url-helper.service';
import { toSignal } from '@angular/core/rxjs-interop';

const scrollToTimeout = 500;

type NativeScrollAlign = 'start' | 'center' | 'end' | 'nearest';
type CustomScrollAlign = 'top';
export type ScrollAlign = NativeScrollAlign | CustomScrollAlign;

type Options = {
    ignoreIfVisible?: boolean;
    align?: ScrollAlign;
    offset?: number;
    behavior?: ScrollBehavior;
};

@Injectable({
    providedIn: 'root'
})
export class ScrollService {
    private readonly scrollHistory: Map<string, [number, number]> = new Map();
    private readonly scrollElement = signal<HTMLElement | null>(null);
    private readonly headerHeight = signal<number>(0);

    public scroll$ = this.getScroll();
    public isScrollingDown$ = this.scrollingDown().pipe(shareReplay(1));
    public hideHeader$: Observable<boolean>;

    public getHeaderHeight = computed(() => this.headerHeight());

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly platformService: PlatformService,
        private readonly router: Router,
        private readonly viewportScroller: ViewportScroller,
        private readonly siteContextService: SiteContextService,
        private readonly urlHelperService: UrlHelperService,
        private readonly ngZone: NgZone
    ) {
        const scrollingPosition: Signal<Scroll | undefined> = toSignal(
            this.router.events.pipe(
                tap(event => {
                    if (event instanceof NavigationStart && event.navigationTrigger === 'imperative') {
                        this.scrollHistory.set(
                            this.urlHelperService.getCurrentUrl().toString(),
                            this.viewportScroller.getScrollPosition()
                        );
                    }
                }),
                filter((event): event is Scroll => event instanceof Scroll),
                map((event: Scroll) => event)
            )
        );

        effect(() => {
            if (this.scrollElement() && scrollingPosition()) {
                this.handleScrollRestoration(
                    scrollingPosition() as Scroll,
                    this.scrollHistory.get(this.urlHelperService.getCurrentUrl().toString()),
                    this.siteContextService.lastNavigationWasPopstate
                );
            }
        });

        afterRender(() => {
            const header: HTMLElement | null = this.document.querySelector('.header');
            if (header) {
                this.headerHeight.set(header.getBoundingClientRect().height);
            } else {
                throw new Error('Header not found');
            }
        });

        this.hideHeader$ = merge(
            this.scrollingDown(350).pipe(shareReplay(1)),
            this.router.events.pipe(
                // Reset scrollingDown if navigation has occured
                filter(event => event instanceof NavigationEnd && this.platformService.isBrowser),
                map(() => false)
            )
        );
    }

    public setScrollElement(element: HTMLElement): void {
        this.scrollElement.set(element);
    }

    public scrollTo(
        element: HTMLElement,
        { ignoreIfVisible = false, align = 'nearest', offset = 0, behavior = 'smooth' }: Options = {}
    ): ReplaySubject<void> {
        const subject = new ReplaySubject<void>(1);
        if (!this.platformService.isBrowser || !element) {
            subject.next();
            subject.complete();
            return subject;
        }

        const topMenuOffset = this.getHeaderHeight();
        const { top: elementTop, bottom: elementBottom, height: elementHeight } = element.getBoundingClientRect();
        const topBelowViewportTop = elementTop > topMenuOffset;
        const bottomAboveViewportBottom = elementBottom <= window.innerHeight;
        const biggerThanViewport = elementHeight > window.innerHeight - topMenuOffset;
        if (!ignoreIfVisible || !topBelowViewportTop || !bottomAboveViewportBottom) {
            const scrollTopToBottom = !bottomAboveViewportBottom && !biggerThanViewport;
            if (!scrollTopToBottom || align === 'top') {
                // If we are going to scroll down, we add the topmenu offset again, since the header disappears on scrollDown
                let newTopPosition = elementTop - topMenuOffset + window.pageYOffset + offset;
                newTopPosition = newTopPosition > window.pageYOffset ? newTopPosition + topMenuOffset : newTopPosition;
                if ('scrollBehavior' in document.documentElement.style) {
                    window.scroll({
                        top: newTopPosition,
                        behavior
                    });
                } else {
                    window.scroll(0, newTopPosition);
                }
            } else {
                if ('scrollBehavior' in document.documentElement.style) {
                    element.scrollIntoView({ behavior, block: align, inline: 'end' });
                } else {
                    element.scrollIntoView();
                }
            }
            race(this.scroll$.pipe(debounceTime(50), take(1)), of(undefined).pipe(delay(scrollToTimeout))).subscribe(
                () => {
                    subject.next();
                    subject.complete();
                }
            );
        } else {
            subject.next();
            subject.complete();
        }
        return subject;
    }

    // Notifies subscribers when scrolling down or up, returns true if scrolling down
    public scrollingDown(topOffset = 0): Observable<boolean> {
        if (this.platformService.isServer) {
            return of<boolean>(false);
        }
        return this.scroll$ // Return the same observable to subscribers
            .pipe(
                throttleTime(10), // Throttle scroll
                map(() => window.pageYOffset),
                filter(scrollValue => scrollValue >= topOffset),
                pairwise(), // Get last and new value as array: [last, new]
                map(([y1, y2]): boolean => Boolean(y1 < y2)),
                distinctUntilChanged() // Only publish if value changes
            );
    }

    // ScrollPositionRestoration: 'enabled', is currently not working as intended:
    // https://github.com/angular/angular/issues/24547
    // This is a temporarily solution for that, this should be removed when the issue is solved
    // Though ensure popstate restore (the first case) is handled also
    public handleScrollRestoration(
        scrollEvent: Scroll,
        lastScrollPosition?: [number, number],
        wasPopstate?: boolean
    ): void {
        // if position is undefined and not null, this means angular lost track of the scroll position.
        // This happens if you replace the url with location.replaceState, and goes back by popstate
        // In that case to ensure that we restore the correct scrollstate, we restore last scroll position
        // which we store here in the scroll service
        const { position, anchor } = scrollEvent;
        if (position === undefined && wasPopstate && lastScrollPosition) {
            this.viewportScroller.scrollToPosition(lastScrollPosition);
        } else if (position) {
            this.viewportScroller.scrollToPosition(position);
        } else if (anchor) {
            this.pollAnchor(anchor, 5);
        } else {
            this.viewportScroller.scrollToPosition([0, 0]);
        }
    }

    /**
     * Method for polling the DOM when angular dynamically render content
     * @param anchor
     * @param tries
     */
    private pollAnchor(anchor: string, tries: number) {
        const element = this.document.getElementById(anchor);

        if (!element && tries > 0 && this.platformService.isBrowser) {
            this.ngZone.runOutsideAngular(() => {
                setTimeout(() => {
                    this.ngZone.run(() => {
                        this.pollAnchor(anchor, tries - 1);
                    });
                }, 100);
            });

            return;
        }

        this.viewportScroller.scrollToAnchor(anchor);
    }

    private getScroll(): Observable<Event> {
        if (this.platformService.isServer) {
            return of<Event>();
        }
        return fromEvent(document, 'scroll');
    }
}
